import { Dayjs } from 'dayjs';
import {
  ReactNode, createContext, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import MainAppContext from '../MainAppContext';
import TimeSeriesContext from '../TimeSeriesContext';
import { utcDayjs } from '../../utils';

interface RealTimeProviderProps {
    children: ReactNode
}

interface ContextValues {
    realTime: Dayjs | null,
    setRealTime: (day: Dayjs | null) => void
}

const RealTimeContext = createContext<ContextValues>({
  realTime: utcDayjs(),
  setRealTime: () => {},
});

function RealTimeProvider({ children }: RealTimeProviderProps) {
  const realTimeRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [realTime, setRealTime] = useState<Dayjs | null>(null);
  const { realTimeMode } = useContext(MainAppContext);
  const { setCurrentPosition } = useContext(TimeSeriesContext);

  useEffect(() => {
    let index = 0;
    if (realTimeMode) {
      realTimeRef.current = setInterval(() => {
        const now = utcDayjs();
        // eslint-disable-next-line no-underscore-dangle
        const divisor = parseInt((window as any)._env_.REACT_APP_REAL_TIME_UPDATE_INTERVAL || '5', 10);
        if (index % divisor === 0) {
          setCurrentPosition(now.toISOString());
        }
        setRealTime(now);
        index += 1;
      }, 1000);
    }

    return () => {
      if (realTimeRef.current) {
        clearInterval(realTimeRef.current);
        realTimeRef.current = null;
        setRealTime(null);
      }
    };
  }, [realTimeMode, setCurrentPosition]);

  return (
    <RealTimeContext.Provider value={useMemo(() => ({
      realTime,
      setRealTime,
    }), [realTime])}
    >
      {children}
    </RealTimeContext.Provider>
  );
}

export { RealTimeProvider };
export default RealTimeContext;
